import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { WarehouseTypeBadge } from './WarehouseTypeBadge';

export interface WarehouseOption {
  id: string;
  code: string;
  name: string;
  type: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country?: string | null;
}

export function formatWarehouseAddress(warehouse: WarehouseOption): string {
  const parts = [
    warehouse.addressLine1,
    warehouse.addressLine2,
    warehouse.city,
    warehouse.postcode,
  ]
    .map((part) => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

interface LocationAssignmentPickerProps {
  warehouses: WarehouseOption[];
  selectedWarehouseIds: string[];
  onSelectedWarehouseIdsChange: (ids: string[]) => void;
  loading?: boolean;
  showScopedWarning?: boolean;
}

export function LocationAssignmentPicker({
  warehouses,
  selectedWarehouseIds,
  onSelectedWarehouseIdsChange,
  loading = false,
  showScopedWarning = false,
}: LocationAssignmentPickerProps) {
  const [search, setSearch] = useState('');

  const filteredWarehouses = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return warehouses;
    return warehouses.filter((warehouse) => {
      const address = formatWarehouseAddress(warehouse).toLowerCase();
      return (
        warehouse.name.toLowerCase().includes(term) ||
        warehouse.code.toLowerCase().includes(term) ||
        warehouse.type.toLowerCase().includes(term) ||
        address.includes(term)
      );
    });
  }, [warehouses, search]);

  const allFilteredSelected =
    filteredWarehouses.length > 0 &&
    filteredWarehouses.every((warehouse) => selectedWarehouseIds.includes(warehouse.id));
  const someFilteredSelected =
    filteredWarehouses.some((warehouse) => selectedWarehouseIds.includes(warehouse.id)) &&
    !allFilteredSelected;

  function toggleWarehouse(warehouseId: string, checked: boolean) {
    if (checked) {
      if (selectedWarehouseIds.includes(warehouseId)) return;
      onSelectedWarehouseIdsChange([...selectedWarehouseIds, warehouseId]);
      return;
    }
    onSelectedWarehouseIdsChange(selectedWarehouseIds.filter((id) => id !== warehouseId));
  }

  function toggleAllFiltered(checked: boolean) {
    const filteredIds = filteredWarehouses.map((warehouse) => warehouse.id);
    if (checked) {
      onSelectedWarehouseIdsChange(Array.from(new Set([...selectedWarehouseIds, ...filteredIds])));
      return;
    }
    onSelectedWarehouseIdsChange(
      selectedWarehouseIds.filter((id) => !filteredIds.includes(id))
    );
  }

  if (loading) {
    return <p className="py-4 text-sm text-muted-foreground">Loading locations…</p>;
  }

  if (warehouses.length === 0) {
    return (
      <p className="py-4 text-sm text-muted-foreground">No active hubs or spokes found.</p>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="relative shrink-0">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name, code, type, or address…"
          className="pl-9"
          aria-label="Search locations"
        />
      </div>

      <p className="shrink-0 text-sm text-muted-foreground">
        {selectedWarehouseIds.length} selected
      </p>

      {filteredWarehouses.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">
          No locations match “{search.trim()}”.
        </p>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={
                      allFilteredSelected
                        ? true
                        : someFilteredSelected
                          ? 'indeterminate'
                          : false
                    }
                    onCheckedChange={(checked) => toggleAllFiltered(checked === true)}
                    aria-label="Select all visible locations"
                  />
                </TableHead>
                <TableHead className="w-24">Type</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredWarehouses.map((warehouse) => {
                const checked = selectedWarehouseIds.includes(warehouse.id);
                return (
                  <TableRow
                    key={warehouse.id}
                    data-state={checked ? 'selected' : undefined}
                    className="cursor-pointer"
                    onClick={() => toggleWarehouse(warehouse.id, !checked)}
                  >
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(value) =>
                          toggleWarehouse(warehouse.id, value === true)
                        }
                        aria-label={`Select ${warehouse.name}`}
                      />
                    </TableCell>
                    <TableCell>
                      <WarehouseTypeBadge type={warehouse.type as 'HUB' | 'SPOKE'} />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{warehouse.name}</div>
                      <div className="text-sm text-muted-foreground">{warehouse.code}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatWarehouseAddress(warehouse)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {showScopedWarning && selectedWarehouseIds.length === 0 && (
        <p className="shrink-0 text-sm text-amber-700">
          Assign at least one warehouse. Without assignments this user will see no location data.
        </p>
      )}
    </div>
  );
}
